import {
  ButtonGroup,
  type ButtonProps,
  DataStatus,
  DialogFooter,
  IconButton,
  useDialogRequestClose,
} from 'platform/components';
import {Heading, HStack, VStack} from 'platform/foundation';

import {useState} from 'react';

import type {BulkOperationResponseBody} from '@dms/api/core';
import i18n from '@dms/i18n';
import {bulkOperationsServices, entityResourceResolver} from '@dms/shared';

import {
  BulkOperationResult,
  getBulkOperationRecoveryAction,
  getBulkOperationTitle,
  useBulkOperationRecovery,
  useBulkOperationTracking,
} from 'features/eag-grid';

export function BulkOperationResultDialogContent(props: {
  bulkOperationId: string;
  actionLabel?: string;
}) {
  const requestClose = useDialogRequestClose();
  const [rerunOperation, setRerunOperation] = useState<BulkOperationResponseBody>();
  const tracking = useBulkOperationTracking({
    services: bulkOperationsServices,
    linkedOperationId: rerunOperation?.bulkOperationId ?? props.bulkOperationId,
    initialOperation: rerunOperation,
  });
  const recovery = useBulkOperationRecovery({
    services: bulkOperationsServices,
    onRegistered: (operation) => setRerunOperation(operation),
  });
  const {operation, isLoading, isError} = tracking.linked;
  const rerunnableOperation =
    operation !== undefined && getBulkOperationRecoveryAction(operation) === 'rerunFailed'
      ? operation
      : null;
  const isRerunPending =
    operation !== undefined && recovery.pendingIds.has(operation.bulkOperationId);
  const actionName =
    props.actionLabel ?? operation?.actionCode ?? i18n.t('general.actions.bulkActions');
  const title = getBulkOperationTitle(actionName, operation?.state);
  const buttons: ButtonProps[] = [
    ...(rerunnableOperation
      ? [
          {
            title: i18n.t('general.actions.rerunFailed', {defaultValue: 'Rerun failed'}),
            suffix: `(${rerunnableOperation.failedCount})`,
            variant: 'outlined' as const,
            isLoading: isRerunPending,
            isDisabled: isRerunPending,
            onClick: () => void recovery.rerun(rerunnableOperation),
          },
        ]
      : []),
    {title: i18n.t('page.datagrid.labels.done'), onClick: requestClose},
  ];

  return (
    <VStack spacing={4}>
      <HStack justify="space-between" align="center" spacing={4}>
        <Heading size={3} headingLevel={2}>
          {title}
        </Heading>
        <IconButton
          icon="navigation/close"
          aria-label={i18n.t('general.actions.close')}
          onClick={requestClose}
        />
      </HStack>
      <DataStatus isLoading={isLoading} isError={isError} minHeight={24}>
        <BulkOperationResult
          services={bulkOperationsServices}
          entityResourceResolver={entityResourceResolver}
          operation={operation}
          actionDefinition={{title: actionName}}
        />
      </DataStatus>
      <DialogFooter>
        <ButtonGroup align="right" buttons={buttons} />
      </DialogFooter>
    </VStack>
  );
}
